import React, { useState, useEffect } from "react";
import { Search, Printer, ArrowRight, AlertCircle } from "lucide-react";
import { api } from "../../utils/api";

export default function DebtorsReport({ onDetailClick }) {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0); 

  useEffect(() => {
    const fetchRealBalances = async () => {
      try {
        const ownersRes = await api.get("/owners");
        const ownersList = ownersRes.data || [];

        const detailedOwners = await Promise.all(
          ownersList.map(async (o) => {
            const code = o.accounting_code || o.hesap_kodu || o.HesapKodu || o.account_code;
            if (!code) return { ...o, real_bakiye: 0, safe_code: "Bilinmiyor" };

            try {
              const statementRes = await api.get(`/statement/${code}`);
              const currentBalance = statementRes.data?.totals?.balance || 0;
              return { ...o, real_bakiye: currentBalance, safe_code: code };
            } catch (err) {
              return { ...o, real_bakiye: 0, safe_code: code };
            }
          })
        );

        setOwners(detailedOwners);
        setLoading(false);
      } catch (err) {
        console.error("Borçlular hesaplanırken hata oluştu:", err);
        setLoading(false);
      }
    };

    fetchRealBalances();
  }, []);

  
  // SADECE GÜNCEL EKSTRE BAKİYESİ KESİNLİKLE 0'DAN BÜYÜK (BORÇLU) OLANLARI FİLTRELE
  const debtors = owners.filter(o => o.real_bakiye > 1 && o.real_bakiye >= threshold)
                        .sort((a, b) => b.real_bakiye - a.real_bakiye);
  const totalDebt = debtors.reduce((sum, o) => sum + o.real_bakiye, 0);

  const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(value);

  const handlePrint = () => {
    window.print();
  };

  // Dinamik Rapor Başlığı
  const printTitle = threshold > 0 
    ? `BAŞKENT SİTESİ/APARTMANI ${formatTL(threshold)} ve ÜZERİ BORCU OLANLAR LİSTESİ` 
    : `BAŞKENT SİTESİ/APARTMANI BORÇLULAR LİSTESİ`;

  if (loading) return (
    <div style={{ padding: "80px", textAlign: "center", color: "#64748b", display: "flex", flexDirection: "column", gap: "16px", alignItems: "center", animation: "fadeIn 0.3s ease-in" }}>
      <div style={{ width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      <div>
        <h3 style={{ margin: "0 0 6px 0", color: "#1e293b" }}>Hesap Dökümleri Taranıyor...</h3>
        <p style={{ margin: 0, fontSize: "14px" }}>Tüm villaların güncel hesap ekstreleri inceleniyor, lütfen bekleyin.</p>
      </div>
    </div>
  );

  return (
    <div className="debtors-report-container" style={{ animation: "fadeIn 0.4s ease-out" }}>
      
      {/* SADECE YAZDIRMA ESNASINDA ÇALIŞAN ÇOK GÜÇLÜ CSS */}
      <style>{`
        @media print {
          /* 1. Sayfadaki her şeyi gizle (Yan menü, üst bar vb.) */
          body * {
            visibility: hidden;
          }
          /* 2. Sadece yazdırılacak alanı ve içindekileri görünür yap */
          .debtors-print-area, .debtors-print-area * {
            visibility: visible;
          }
          /* 3. Yazdırılacak alanı ekranın en sol üst köşesine taşı (Tam A4 boyutu için) */
          .debtors-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          /* 4. Butonları, arama çubuğunu vb. kalıcı olarak gizle */
          .debtors-print-area .no-print {
            display: none !important;
          }
          /* 5. Resmi başlığı göster */
          .print-header {
            display: block !important;
            text-align: center;
            margin-bottom: 25px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
          }
          .print-header h2 {
            font-size: 20px;
            margin: 0 0 8px 0;
            color: #000;
          }
          .print-header p {
            font-size: 14px;
            margin: 0;
            color: #333;
          }
          /* 6. Tablo kenarlıklarını siyah ve net yap */
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000 !important; padding: 10px !important; color: #000 !important; }
          th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; }
        }
        
        /* Ekranda (Tarayıcıda) resmi başlığı gizle */
        .print-header { display: none; }
      `}</style>

      {/* ARAÇ ÇUBUĞU VE FİLTRE (Yazdırmada Gizlenir) */}
      <div className="no-print" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "20px 24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", marginBottom: "24px", gap: "20px" }}>
        
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
            <AlertCircle size={24} />
          </div>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: "18px", color: "#1e293b" }}>Borçlular Listesi</h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>Toplam {debtors.length} kayıt listeleniyor</p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f8fafc", padding: "6px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Search size={16} color="#64748b" />
            <span style={{ fontSize: "13px", color: "#475569", fontWeight: "500" }}>Süzgeç (TL):</span>
            <input 
              type="number" 
              value={threshold} 
              onChange={e => setThreshold(Number(e.target.value))}
              style={{ width: "80px", background: "transparent", border: "none", outline: "none", fontWeight: "bold", color: "#1e293b" }}
            />
            <span style={{ fontSize: "13px", color: "#475569" }}>ve üzeri</span>
          </div>
          <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#1e293b", color: "white", border: "none", padding: "10px 16px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", transition: "background 0.2s" }}>
            <Printer size={18} /> PDF / Yazdır
          </button>
        </div>
      </div>

      {/* YAZDIRILACAK ALAN (PRINT AREA) */}
      <div className="debtors-print-area">
        
        {/* Yazdırma Ekranında Belirecek Dinamik Resmi Başlık */}
        <div className="print-header">
          <h2>{printTitle}</h2>
          <p>Rapor Tarihi: {new Date().toLocaleDateString("tr-TR")} - Saat: {new Date().toLocaleTimeString("tr-TR", { hour: '2-digit', minute:'2-digit' })}</p>
        </div>

        {/* LİSTE / TABLO */}
        <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", overflow: "hidden" }} className="table-container">
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Daire Adı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Hesap Kodu</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Malik Adı Soyadı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Oturan Adı Soyadı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "right" }}>Borç Tutarı</th>
                  <th className="no-print" style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "center" }}>Ayrıntı</th>
                </tr>
              </thead>
              <tbody>
                {debtors.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>Belirlenen limite uygun borçlu bulunamadı.</td>
                  </tr>
                ) : debtors.map((o, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: "14px 16px", fontWeight: "700", color: "#334155" }}>
                      {o.villa_no || o.daire_kodu || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "14px" }}>
                      {o.safe_code}
                    </td>
                    <td style={{ padding: "14px 16px", fontWeight: "500", color: "#1e293b" }}>
                      {o.owner_name || o.malik_adi || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#475569" }}>
                      {o.tenant_name || o.oturan_adi || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: "bold", color: "#ef4444", fontSize: "15px" }}>
                      {formatTL(o.real_bakiye)}
                    </td>
                    <td className="no-print" style={{ padding: "14px 16px", textAlign: "center" }}>
                      <button 
                        onClick={() => onDetailClick(o.safe_code)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}
                      >
                        Ekstre <ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#fef2f2", borderTop: "2px solid #fecaca" }}>
                  <td colSpan="4" style={{ padding: "16px", textAlign: "right", fontWeight: "bold", color: "#991b1b" }}>GENEL TOPLAM:</td>
                  <td style={{ padding: "16px", textAlign: "right", fontWeight: "900", color: "#b91c1c", fontSize: "18px" }}>{formatTL(totalDebt)}</td>
                  <td className="no-print"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </div> {/* YAZDIRILACAK ALAN SONU */}

    </div>
  );
}